import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Plus, Edit2, Phone, Mail, MapPin } from 'lucide-react'
import { supabase } from '../lib/supabase'

const STATUS_LABELS = {
  new_lead:'New Lead', contacted:'Contacted', meeting_confirmed:'Meeting Set',
  measurements_taken:'Measured', order_confirmed:'Confirmed',
  in_production:'In Production', quality_check:'Quality Check',
  shipped:'Shipped', delivered:'Delivered', cancelled:'Cancelled'
}
const STATUS_COLORS = {
  new_lead:'gray', contacted:'blue', meeting_confirmed:'blue',
  measurements_taken:'amber', order_confirmed:'amber',
  in_production:'gold', quality_check:'gold',
  shipped:'green', delivered:'green', cancelled:'red'
}

const MEASUREMENT_SECTIONS = {
  'Suit / Jacket': [
    { key:'chest', label:'Chest' }, { key:'waist', label:'Waist' }, { key:'hips', label:'Hips' },
    { key:'shoulders', label:'Shoulders' }, { key:'back_length', label:'Back Length' },
    { key:'front_length', label:'Front Length' }, { key:'sleeve_right', label:'Sleeve (R)' },
    { key:'sleeve_left', label:'Sleeve (L)' }, { key:'bicep', label:'Bicep' },
    { key:'wrist', label:'Wrist' }, { key:'neck', label:'Neck' },
  ],
  'Trouser': [
    { key:'trouser_waist', label:'Waist' }, { key:'seat', label:'Seat' },
    { key:'thigh', label:'Thigh' }, { key:'knee', label:'Knee' },
    { key:'inseam', label:'Inseam' }, { key:'outseam', label:'Outseam' },
    { key:'bottom_width', label:'Bottom' }, { key:'rise_front', label:'Rise (F)' },
    { key:'rise_back', label:'Rise (B)' },
  ],
  'Shirt': [
    { key:'collar', label:'Collar' }, { key:'shirt_chest', label:'Chest' },
    { key:'shirt_waist', label:'Waist' }, { key:'shirt_length', label:'Length' },
    { key:'shirt_sleeve', label:'Sleeve' },
  ],
}

export default function ClientProfile() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [client, setClient] = useState(null)
  const [measurements, setMeasurements] = useState([])
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('info')
  const [measureIdx, setMeasureIdx] = useState(0)
  const [editing, setEditing] = useState(false)
  const [editForm, setEditForm] = useState({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetchAll()
  }, [id])

  async function fetchAll() {
    const [
      { data: c },
      { data: m },
      { data: o }
    ] = await Promise.all([
      supabase.from('clients').select('*').eq('id', id).single(),
      supabase.from('measurements').select('*').eq('client_id', id).order('measured_at', { ascending: false }),
      supabase.from('orders').select('*, fabrics(fabric_code, name)').eq('client_id', id).order('order_date', { ascending: false, nullsFirst: false }).order('created_at', { ascending: false })
    ])
    setClient(c)
    setEditForm(c || {})
    setMeasurements(m || [])
    setMeasureIdx(0)
    setOrders(o || [])
    setLoading(false)
  }

  async function saveEdit() {
    setSaving(true)
    // Send only editable columns (phone_digits etc. are computed by the database).
    const EDITABLE = ['first_name','last_name','email','phone','whatsapp','company','job_title','country','city','notes']
    const patch = Object.fromEntries(EDITABLE.map(k => [k, editForm[k] === '' ? null : (editForm[k] ?? null)]))
    const { data, error } = await supabase.from('clients').update(patch).eq('id', id).select().single()
    if (!error) {
      setClient(data)
      setEditForm(data)
      setEditing(false)
    } else {
      alert('Could not save: ' + error.message)
    }
    setSaving(false)
  }

  function getInitials(c) {
    return ((c?.first_name||'').charAt(0) + (c?.last_name||'').charAt(0)).toUpperCase() || '?'
  }

  if (loading) return <div className="empty-state"><p style={{ color: 'var(--gray-400)' }}>Loading…</p></div>
  if (!client) return <div className="empty-state"><p>Client not found</p></div>

  const fullName = [client.first_name, client.last_name].filter(Boolean).join(' ') || 'No name'
  // The set being viewed. Index 0 is the most recent by measurement date.
  const latestMeasurements = measurements[measureIdx] || measurements[0]
  const orderById = Object.fromEntries(orders.map(o => [o.id, o]))
  function measureLabel(m) {
    const o = m.order_id && orderById[m.order_id]
    const date = new Date(m.measured_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    const ref = o ? (o.legacy_order_number ? `#${o.legacy_order_number}` : o.order_number) : null
    return ref ? `${date} · ${ref}` : date
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <button className="page-header-back" onClick={() => navigate(-1)}>←</button>
        <div className="list-item-avatar" style={{ width: '44px', height: '44px', fontSize: '18px', flexShrink: 0 }}>{getInitials(client)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ fontSize: '18px', fontWeight: '700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fullName}</h1>
          <div style={{ display: 'flex', gap: '6px', marginTop: '3px', flexWrap: 'wrap' }}>
            {client.contact_type && <span className={`badge ${client.contact_type === 'Past Client' ? 'badge-green' : client.contact_type === 'VIP' ? 'badge-gold' : 'badge-gray'}`}>{client.contact_type}</span>}
            {client.region_bucket && <span className="badge badge-gray">{client.region_bucket}</span>}
          </div>
        </div>
        <button className="btn btn-outline btn-sm btn-icon" onClick={() => setEditing(e => !e)}>
          <Edit2 size={15} />
        </button>
      </div>

      {/* Quick contact actions */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {client.phone && (
          <a href={`tel:${client.phone}`} className="btn btn-outline" style={{ flex: 1, justifyContent: 'center' }}>
            <Phone size={15} /> Call
          </a>
        )}
        {(client.whatsapp || client.phone) && (
          <a href={`https://wa.me/${(client.whatsapp || client.phone).replace(/\D/g,'')}`} target="_blank" rel="noreferrer"
            className="btn btn-outline" style={{ flex: 1, justifyContent: 'center' }}>
            💬 WhatsApp
          </a>
        )}
        {client.email && (
          <a href={`mailto:${client.email}`} className="btn btn-outline" style={{ flex: 1, justifyContent: 'center' }}>
            <Mail size={15} /> Email
          </a>
        )}
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button className={`tab ${tab === 'info' ? 'active' : ''}`} onClick={() => setTab('info')}>Info</button>
        <button className={`tab ${tab === 'measurements' ? 'active' : ''}`} onClick={() => setTab('measurements')}>
          Measurements {measurements.length > 0 && `(${measurements.length})`}
        </button>
        <button className={`tab ${tab === 'orders' ? 'active' : ''}`} onClick={() => setTab('orders')}>
          Orders {orders.length > 0 && `(${orders.length})`}
        </button>
      </div>

      {/* ── INFO TAB ── */}
      {tab === 'info' && (
        <div className="fade-in">
          {editing ? (
            <div className="card" style={{ marginBottom: '12px' }}>
              <div className="form-section-title">Edit Details</div>
              {[
                ['first_name','First Name'], ['last_name','Last Name'],
                ['email','Email'], ['phone','Phone'], ['whatsapp','WhatsApp'],
                ['company','Company'], ['job_title','Job Title'],
                ['country','Country'], ['city','City'],
              ].map(([k, label]) => (
                <div className="form-group" key={k}>
                  <label className="form-label">{label}</label>
                  <input className="form-input" value={editForm[k] || ''} onChange={e => setEditForm(p => ({ ...p, [k]: e.target.value }))} />
                </div>
              ))}
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" value={editForm.notes || ''} onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} rows={3} />
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn btn-primary" style={{ flex: 1 }} onClick={saveEdit} disabled={saving}>
                  {saving ? 'Saving…' : '✓ Save'}
                </button>
                <button className="btn btn-outline" onClick={() => setEditing(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <div className="card" style={{ marginBottom: '12px' }}>
              <div className="form-section-title">Contact Details</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {client.email && (
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <Mail size={15} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
                    <span style={{ fontSize: '14px', wordBreak: 'break-all' }}>{client.email}</span>
                  </div>
                )}
                {client.phone && (
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <Phone size={15} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
                    <span style={{ fontSize: '14px' }}>{client.phone}</span>
                  </div>
                )}
                {(client.city || client.country) && (
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <MapPin size={15} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
                    <span style={{ fontSize: '14px' }}>{[client.city, client.country].filter(Boolean).join(', ')}</span>
                  </div>
                )}
                {client.company && (
                  <div style={{ fontSize: '14px' }}><span style={{ color: 'var(--gray-400)' }}>Company: </span>{client.company}{client.job_title && ` · ${client.job_title}`}</div>
                )}
                {client.referral_source && (
                  <div style={{ fontSize: '14px' }}><span style={{ color: 'var(--gray-400)' }}>Referred by: </span>{client.referral_source}</div>
                )}
                {client.location_code && (
                  <div style={{ fontSize: '12px', color: 'var(--gray-400)' }}>Code: {client.location_code}</div>
                )}
              </div>
              {client.notes && (
                <div style={{ marginTop: '12px', padding: '10px', background: 'var(--gray-50)', borderRadius: '8px', fontSize: '13px', color: 'var(--gray-600)', lineHeight: '1.5' }}>
                  {client.notes}
                </div>
              )}
            </div>
          )}

          {/* Stats */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value" style={{ color: 'var(--brown-dark)' }}>{orders.length}</div>
              <div className="stat-label">Total Orders</div>
            </div>
            <div className="stat-card gold-card">
              <div className="stat-value">{orders.filter(o => o.status === 'delivered').length}</div>
              <div className="stat-label">Delivered</div>
            </div>
          </div>

          {/* New order CTA */}
          <Link to={`/orders/new`} className="btn btn-primary btn-lg" style={{ borderRadius: '12px', marginTop: '4px', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <Plus size={18} /> New Order for {client.first_name || 'Client'}
          </Link>
        </div>
      )}

      {/* ── MEASUREMENTS TAB ── */}
      {tab === 'measurements' && (
        <div className="fade-in">
          {measurements.length === 0 ? (
            <div className="empty-state card">
              <p className="empty-state-title">No measurements yet</p>
              <p className="empty-state-sub">Measurements are added when creating an order</p>
            </div>
          ) : (
            <>
              {measurements.length > 1 && (
                <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '6px', marginBottom: '8px' }}>
                  {measurements.map((m, i) => (
                    <button key={m.id} type="button" onClick={() => setMeasureIdx(i)} style={{
                      flexShrink: 0, padding: '6px 12px', borderRadius: '20px', cursor: 'pointer', fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap',
                      border: 'none', boxShadow: 'var(--shadow)',
                      background: i === measureIdx ? 'var(--brown-dark)' : 'var(--white)',
                      color: i === measureIdx ? 'var(--gold)' : 'var(--gray-600)',
                    }}>
                      {i === 0 ? 'Latest · ' : ''}{measureLabel(m)}{m.source === 'ledger' ? ' 📖' : ''}
                    </button>
                  ))}
                </div>
              )}
              {latestMeasurements && (
                <div style={{ marginBottom: '8px', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span className={`badge ${measureIdx === 0 ? 'badge-green' : 'badge-gray'}`}>{measureIdx === 0 ? 'Latest' : 'Older set'}</span>
                  {latestMeasurements.order_id && orderById[latestMeasurements.order_id] && (
                    <Link to={`/orders/${latestMeasurements.order_id}`} style={{ fontSize: '12px', color: 'var(--gold)' }}>
                      from order {orderById[latestMeasurements.order_id].legacy_order_number ? `#${orderById[latestMeasurements.order_id].legacy_order_number}` : orderById[latestMeasurements.order_id].order_number}
                    </Link>
                  )}
                  <span style={{ fontSize: '12px', color: 'var(--gray-400)' }}>
                    {new Date(latestMeasurements.measured_at).toLocaleDateString()}
                  </span>
                  <span style={{ fontSize: '12px', color: 'var(--gray-400)' }}>· All in inches (in)</span>
                </div>
              )}
              {Object.entries(MEASUREMENT_SECTIONS).map(([section, fields]) => {
                const hasAny = fields.some(f => latestMeasurements?.[f.key])
                if (!hasAny) return null
                return (
                  <div className="card" key={section} style={{ marginBottom: '12px' }}>
                    <div className="form-section-title">{section}</div>
                    <div className="measure-grid">
                      {fields.map(({ key, label }) => {
                        const val = latestMeasurements?.[key]
                        if (!val) return null
                        return (
                          <div key={key} className="measure-field">
                            <span className="measure-label">{label}</span>
                            <span className="measure-value">{val}<span className="measure-unit"> in</span></span>
                          </div>
                        )
                      })}
                    </div>
                    {latestMeasurements?.posture_notes && section === 'Suit / Jacket' && (
                      <div style={{ marginTop: '10px', padding: '8px', background: 'var(--gray-50)', borderRadius: '8px', fontSize: '12px', color: 'var(--gray-600)' }}>
                        <strong>Posture: </strong>{latestMeasurements.posture_notes}
                      </div>
                    )}
                    {latestMeasurements?.fit_notes && section === 'Suit / Jacket' && (
                      <div style={{ marginTop: '6px', padding: '8px', background: 'var(--gray-50)', borderRadius: '8px', fontSize: '12px', color: 'var(--gray-600)' }}>
                        <strong>Fit prefs: </strong>{latestMeasurements.fit_notes}
                      </div>
                    )}
                  </div>
                )
              })}
              {latestMeasurements?.height && (
                <div className="card" style={{ marginBottom: '12px' }}>
                  <div className="form-section-title">General</div>
                  <div className="measure-grid">
                    {latestMeasurements.height && <div className="measure-field"><span className="measure-label">Height</span><span className="measure-value">{latestMeasurements.height}<span className="measure-unit"> in</span></span></div>}
                    {latestMeasurements.weight && <div className="measure-field"><span className="measure-label">Weight</span><span className="measure-value">{latestMeasurements.weight}<span className="measure-unit"> lbs</span></span></div>}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── ORDERS TAB ── */}
      {tab === 'orders' && (
        <div className="fade-in">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
            <Link to="/orders/new" className="btn btn-primary btn-sm"><Plus size={15} /> New Order</Link>
          </div>
          {orders.length === 0 ? (
            <div className="empty-state card">
              <p className="empty-state-title">No orders yet</p>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {orders.map(order => (
                <Link key={order.id} to={`/orders/${order.id}`} className="list-item">
                  <div className="list-item-avatar" style={{ borderRadius: '8px', fontSize: '11px', fontWeight: '700', width: '38px', height: '38px' }}>
                    {order.garment_type?.substring(0,3).toUpperCase()}
                  </div>
                  <div className="list-item-content">
                    <div className="list-item-title">
                      {order.order_number}
                      {order.legacy_order_number && <span style={{ fontSize: '11px', color: 'var(--gold)', fontWeight: '600', marginLeft: '6px' }}>#{order.legacy_order_number}</span>}
                      {order.is_historical && <span className="badge badge-gray" style={{ fontSize: '9px', marginLeft: '6px' }}>Book</span>}
                    </div>
                    <div className="list-item-sub">
                      {order.garment_type}
                      {order.fabrics ? ` · ${order.fabrics.name}` : ''}
                      {order.order_date ? ` · ${new Date(order.order_date).toLocaleDateString('en-US',{month:'short',year:'numeric'})}` : ''}
                    </div>
                  </div>
                  <div className="list-item-right">
                    <span className={`badge badge-${STATUS_COLORS[order.status]||'gray'}`} style={{ fontSize: '9px' }}>
                      {STATUS_LABELS[order.status]}
                    </span>
                    {order.price && <span style={{ fontSize: '12px', fontWeight: '600' }}>{order.currency} {Number(order.price).toLocaleString()}</span>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
